/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FiUploadCloud, FiFileText, FiCheck, FiPaperclip, FiTrash2 } from "react-icons/fi";
import { FaArrowLeft } from "react-icons/fa";

import { ThreeDots } from "react-loader-spinner";
import ActionButton from "../../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../../Common/FormComponents";
import { CreateGrnApi } from "../../../../Api/Operations/PurchaseOrders";
import { getPopulateUsers } from "../../../../Redux/actions/accountsactions/getContractComments";
import { getallPurchaseOrders } from "../../../../Redux/actions/Operationsactions/getpurchaseorders";
import { getPurchaseOrderById } from "../../../../Redux/actions/Operationsactions/getpurchaseorderbyid";
import { filterStoreProjects } from "../../projectFilters";
import { useSession } from "../../../../context/SessionContext";
import "./AddGrn.css";
import "../../AddContractorsAndVendors.css";

// Backend columns are numeric(18,2); round so float noise (0.1+0.2) never fails the equality check.
const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export default function AddGrn() {
  const history = useHistory();
  // return to the screen the user came from (PO details, Procurement or Store GRN list)
  const goBackToGrn = () =>
    history.length > 1 ? history.goBack() : history.push("/procurement/GRN");
  const location = useLocation();
  const dispatch = useDispatch();
  const { session } = useSession();

  // State from location or store
  const populateUsers = useSelector((s) => s.getAllPopulateUsers?.data || []);
  const allPOs = useSelector((s) => s.getAllPurchaseOrders?.data || []);
  const rolesData = useSelector((s) => s.GetAllRole);
  const projects = filterStoreProjects(rolesData?.data?.Projects);

  // If navigated with existing poData (from details page or table)
  const initialPoData = location.state?.poData || location.state || null;
  const [selectedPoId, setSelectedPoId] = useState(
    initialPoData?.Id || initialPoData?.PurchaseOrderId || "",
  );
  const [currentPo, setCurrentPo] = useState(initialPoData);
  const [isLoadingPo, setIsLoadingPo] = useState(false);

  // Section 1: Gate & Receipt Details
  const getTodayDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const [grnDate, setGrnDate] = useState(getTodayDate());
  const [vendorChallanNo, setVendorChallanNo] = useState("");
  const [receivingProjectId, setReceivingProjectId] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const [receivedBy, setReceivedBy] = useState(session?.UserName || "");
  const [weighbridgeSlipNo, setWeighbridgeSlipNo] = useState("");

  // Section 2: Items Received
  const [lines, setLines] = useState([]);
  const [qtyReceivedMap, setQtyReceivedMap] = useState({});
  const [qtyAcceptedMap, setQtyAcceptedMap] = useState({});
  const [qtyRejectedMap, setQtyRejectedMap] = useState({});
  const [remarksMap, setRemarksMap] = useState({});

  // Section 3: Evidence & General Remarks
  const [attachedFile, setAttachedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [generalRemarks, setGeneralRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Approvers dropdown state
  const [showApproverDropdown, setShowApproverDropdown] = useState(false);
  const [approverUserIds, setApproverUserIds] = useState([]);
  const [approverSearchTerm, setApproverSearchTerm] = useState("");
  const approverDropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch all POs if not already loaded so user can select
  useEffect(() => {
    if (!allPOs || allPOs.length === 0) {
      dispatch(getallPurchaseOrders());
    }
  }, [dispatch]);

  // When PO is selected or changes, load details
  useEffect(() => {
    if (!selectedPoId) {
      return;
    }

    let cancelled = false;
    const loadPoDetails = async () => {
      // If we already have lines in currentPo matching this ID, use it
      if (
        currentPo &&
        (currentPo.Id === selectedPoId || currentPo.PurchaseOrderId === selectedPoId) &&
        currentPo.Lines &&
        currentPo.Lines.length > 0
      ) {
        initPoFields(currentPo);
        return;
      }

      setIsLoadingPo(true);
      try {
        const res = await dispatch(getPurchaseOrderById(selectedPoId));
        if (cancelled) {
          return;
        }
        if (res?.data?.IsSuccess === false) {
          toast.error(res.data.Message || "Failed to load Purchase Order");
          setSelectedPoId("");
          setCurrentPo(null);
          setLines([]);
          return;
        }
        const poDetails = res?.data?.Data || null;
        if (poDetails) {
          setCurrentPo(poDetails);
          initPoFields(poDetails);
        } else {
          // Fallback to item in allPOs list
          const found = allPOs?.find((p) => (p.Id || p.PurchaseOrderId) === selectedPoId);
          if (found) {
            setCurrentPo(found);
            initPoFields(found);
          }
        }
      } catch (err) {
        console.error("Error loading PO details:", err);
      } finally {
        if (!cancelled) {
          setIsLoadingPo(false);
        }
      }
    };

    loadPoDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedPoId]);

  // Initialize form fields when PO data is loaded
  const initPoFields = (po) => {
    const poLines = po?.Lines || [];
    setLines(poLines);

    // Site / Store default: the PO's project
    if (po?.ProjectId) {
      setReceivingProjectId(String(po.ProjectId));
    }

    // Load approvers for this PO's project
    const projId = po?.ProjectId || session?.ProjectId;
    if (projId) {
      dispatch(getPopulateUsers(projId));
    }

    // Populate initial lines state
    const initRec = {};
    const initAcc = {};
    const initRej = {};
    const initRem = {};

    poLines.forEach((l, idx) => {
      const id = l.Id || l.poLineId || `line_${idx}`;
      const rem = Number(l.RemainingQuantity ?? l.Quantity ?? l.quantity ?? 0);
      initRec[id] = rem;
      initAcc[id] = rem;
      initRej[id] = 0;
      initRem[id] = "";
    });

    setQtyReceivedMap(initRec);
    setQtyAcceptedMap(initAcc);
    setQtyRejectedMap(initRej);
    setRemarksMap(initRem);
  };

  // Close approver dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (approverDropdownRef.current && !approverDropdownRef.current.contains(event.target)) {
        setShowApproverDropdown(false);
      }
    }
    if (showApproverDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showApproverDropdown]);

  // Calculations for line items
  const handleQtyReceivedChange = (id, val) => {
    const num = val === "" ? "" : Number(val);
    setQtyReceivedMap((prev) => ({ ...prev, [id]: num }));

    // Auto-update Accepted and Rejected
    if (num === "") {
      setQtyAcceptedMap((prev) => ({ ...prev, [id]: "" }));
      setQtyRejectedMap((prev) => ({ ...prev, [id]: 0 }));
    } else {
      const currentRejected = Number(qtyRejectedMap[id] || 0);
      const newAccepted = Math.max(0, r2(num - currentRejected));
      setQtyAcceptedMap((prev) => ({ ...prev, [id]: newAccepted }));
    }
  };

  const handleQtyAcceptedChange = (id, val) => {
    const num = val === "" ? "" : Number(val);
    setQtyAcceptedMap((prev) => ({ ...prev, [id]: num }));

    const received = Number(qtyReceivedMap[id] || 0);
    if (num !== "" && received >= num) {
      setQtyRejectedMap((prev) => ({ ...prev, [id]: r2(received - num) }));
    }
  };

  const handleQtyRejectedChange = (id, val) => {
    const num = val === "" ? "" : Number(val);
    setQtyRejectedMap((prev) => ({ ...prev, [id]: num }));

    const received = Number(qtyReceivedMap[id] || 0);
    if (num !== "" && received >= num) {
      setQtyAcceptedMap((prev) => ({ ...prev, [id]: r2(received - num) }));
    }
  };

  const handleRemarksChange = (id, val) => {
    setRemarksMap((prev) => ({ ...prev, [id]: val }));
  };

  // Approver selection helpers
  const toggleApprover = (userId) => {
    setApproverUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const removeApprover = (userId) => {
    setApproverUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const filteredUsers = (populateUsers || []).filter(
    (u) =>
      u?.FullName?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
      u?.Email?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
      u?.Id?.toLowerCase().includes(approverSearchTerm.toLowerCase()),
  );

  const processSelectedFile = (file) => {
    setAttachedFile(file);
    if (file.type && file.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setFilePreview(previewUrl);
    } else {
      setFilePreview(null);
    }
    toast.info(`Attached: ${file.name}`);
  };

  const handleRemoveFile = (e) => {
    e?.stopPropagation?.();
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
    }
    setAttachedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) {
      return "";
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Validation
  const validate = () => {
    if (!selectedPoId) {
      toast.error("Please select a Purchase Order first");
      return false;
    }
    if (!grnDate) {
      toast.error("GRN Date is required");
      return false;
    }
    if (!vendorChallanNo.trim()) {
      toast.error("Vendor Challan / Bilty # is required");
      return false;
    }
    if (!receivingProjectId) {
      toast.error("Receiving Site / Store is required");
      return false;
    }
    if (!generalRemarks || !generalRemarks.trim()) {
      toast.error("General Remarks / Note is required");
      return false;
    }

    if (!lines || lines.length === 0) {
      toast.error("No items found in the selected Purchase Order");
      return false;
    }

    // Line items validation
    let hasReceivedItem = false;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const id = l.Id || l.poLineId || `line_${i}`;
      const rec = Number(qtyReceivedMap[id] ?? 0);
      const maxRem = Number(l.RemainingQuantity ?? l.Quantity ?? l.quantity ?? 0);
      const accepted = Number(qtyAcceptedMap[id] ?? 0);
      const rejected = Number(qtyRejectedMap[id] ?? 0);
      const remark = remarksMap[id] || "";

      if (rec > maxRem) {
        toast.error(
          `Item "${l.ItemDescription || l.ItemName || i + 1}": Received Qty (${rec}) cannot exceed Remaining Qty (${maxRem})`,
        );
        return false;
      }
      if (accepted > rec) {
        toast.error(
          `Item "${l.ItemDescription || l.ItemName || i + 1}": Accepted Qty cannot exceed Received Qty`,
        );
        return false;
      }
      if (r2(accepted + rejected) !== r2(rec)) {
        toast.error(
          `Item "${l.ItemDescription || l.ItemName || i + 1}": Accepted + Rejected must equal Received Qty`,
        );
        return false;
      }
      if (rec > 0) {
        hasReceivedItem = true;
        if (!remark.trim()) {
          toast.error(
            `Item "${l.ItemDescription || l.ItemName || i + 1}": Remarks required for received items`,
          );
          return false;
        }
      }
      // Rejected rows always need a reason (kept for backend/spec), even if Received is 0
      if (rejected > 0 && !remark.trim()) {
        toast.error(
          `Item "${l.MaterialName || l.ItemDescription || i + 1}": Remarks required for rejected items`,
        );
        return false;
      }
    }

    if (!hasReceivedItem) {
      toast.error("At least one item must have a Received Quantity greater than 0");
      return false;
    }

    return true;
  };

  // Submit Handler
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      // Build ISO Date
      const isoDate = new Date(grnDate).toISOString();

      const payload = new FormData();
      payload.append("PurchaseOrderId", selectedPoId);
      payload.append("GrnDate", isoDate);
      payload.append("Narration", generalRemarks);
      payload.append("VendorChallanNo", vendorChallanNo);
      payload.append("ReceivingProjectId", receivingProjectId);
      payload.append("VehicleNo", vehicleNo);
      payload.append("ReceivedBy", receivedBy);
      payload.append("WeighbridgeSlipNo", weighbridgeSlipNo);
      if (attachedFile) {
        payload.append("File", attachedFile);
      }

      // Only lines that actually received something are sent.
      lines
        .map((l, i) => ({ l, id: l.Id || l.poLineId || `line_${i}` }))
        .filter(({ id }) => Number(qtyReceivedMap[id] ?? 0) > 0)
        .forEach(({ l, id }, index) => {
          payload.append(`Lines[${index}].PoLineId`, l.Id || l.poLineId);
          payload.append(`Lines[${index}].QuantityReceived`, String(r2(qtyReceivedMap[id] ?? 0)));
          payload.append(`Lines[${index}].QuantityAccepted`, String(r2(qtyAcceptedMap[id] ?? 0)));
          payload.append(`Lines[${index}].QuantityRejected`, String(r2(qtyRejectedMap[id] ?? 0)));
          payload.append(`Lines[${index}].Remarks`, remarksMap[id] || "");
        });

      approverUserIds.forEach((approverUserId, index) => {
        payload.append(`Approvers[${index}].ApproverUserId`, approverUserId);
      });

      const res = await CreateGrnApi(payload);
      const json = res?.data || res;

      if (json?.IsSuccess) {
        toast.success(json.Message || "GRN created successfully!");
        goBackToGrn();
      } else {
        toast.error(json?.Message || "Failed to create GRN");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating GRN. Please check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  // Header PO label
  const poNumberDisplay = currentPo?.PoNumber || (selectedPoId ? `PO #${selectedPoId}` : "");
  const vendorNameDisplay = currentPo?.VendorName || "";
  const headerSubtitle =
    poNumberDisplay && vendorNameDisplay
      ? `(PO: ${poNumberDisplay} - ${vendorNameDisplay})`
      : poNumberDisplay
        ? `(${poNumberDisplay})`
        : "";

  return (
    <div className="grn-screen-container">
      <div className="col-md-12   text-left page_heading">
        <div className="d-flex align-items-center">
          <FaArrowLeft
            size={16}
            style={{ cursor: "pointer", marginRight: "12px" }}
            title="Back"
            onClick={goBackToGrn}
          />
          <h2 className="grn-header-title">
            <span>Generate GRN</span>
            {headerSubtitle && <span style={{ fontWeight: 600 }}>{headerSubtitle}</span>}
          </h2>
        </div>
      </div>
      <div className="grn-form-card">
        {/* PO Selection Bar (when PO is not pre-fixed or to switch PO) */}
        <div className="grn-po-selector-strip">
          <div className="grn-po-selector-label">
            <FiFileText size={17} />
            <span>Select Purchase Order (PO):</span>
          </div>
          <select
            className="grn-po-select-input"
            value={selectedPoId}
            onChange={(e) => setSelectedPoId(e.target.value)}
          >
            <option value="">-- Choose Purchase Order --</option>
            {allPOs
              ?.filter((po) => po.Status === "Approved" || po.Status === "Pending")
              .map((po) => (
                <option key={po.Id || po.PurchaseOrderId} value={po.Id || po.PurchaseOrderId}>
                  {po.PoNumber} - {po.VendorName} ({po.ProjectName || "General"})
                </option>
              ))}
          </select>
        </div>

        {isLoadingPo ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <ThreeDots height="50" width="50" color="#0d9488" ariaLabel="loading" />
          </div>
        ) : (
          <>
            {/* SECTION 1: Gate & Receipt Details */}
            <h3 className="grn-section-title">Gate & Receipt Details</h3>
            <div className="row mb-2">
              <InputField
                label="GRN Date:"
                name="grnDate"
                type="date"
                value={grnDate}
                onChange={(e) => setGrnDate(e.target.value)}
                required
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />

              <InputField
                label="Vendor Challan #:"
                name="vendorChallanNo"
                placeholder="e.g. CH-88921"
                value={vendorChallanNo}
                onChange={(e) => setVendorChallanNo(e.target.value)}
                required
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />
              <SelectField
                label="Receiving Site:"
                name="receivingProjectId"
                value={receivingProjectId}
                onChange={(e) => setReceivingProjectId(e.target.value)}
                options={[
                  { value: "", label: "Select Receiving Site" },
                  ...(projects || []).map((p) => ({
                    value: String(p.ProjectId),
                    label: p.ProjectName,
                  })),
                  ...(receivingProjectId &&
                  !(projects || []).some((p) => String(p.ProjectId) === receivingProjectId)
                    ? [{ value: receivingProjectId, label: `Project #${receivingProjectId}` }]
                    : []),
                ]}
                required
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />

              <InputField
                label="Vehicle / Truck No:"
                name="vehicleNo"
                placeholder="e.g. FDZ-1249"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />
              <InputField
                label="Received By:"
                name="receivedBy"
                placeholder="Tariq (Storekeeper)"
                value={receivedBy}
                onChange={(e) => setReceivedBy(e.target.value)}
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />

              <InputField
                label="Weighbridge Slip:"
                name="weighbridgeSlipNo"
                placeholder="WB-4410 (Optional)"
                value={weighbridgeSlipNo}
                onChange={(e) => setWeighbridgeSlipNo(e.target.value)}
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
            </div>

            {/* SECTION 2: Items Received */}
            <h3 className="grn-section-title">Items Received</h3>
            <div className="grn-items-table-wrapper">
              <table className="grn-items-table">
                <thead>
                  <tr>
                    <th style={{ width: "24%" }}>Item Name</th>
                    <th style={{ width: "8%" }}>UOM</th>
                    <th style={{ width: "8%" }}>PO Qty</th>
                    <th style={{ width: "9%" }}>Remaining</th>
                    <th style={{ width: "12%" }}>
                      Qty Received <span className="grn-field-required">*</span>
                    </th>
                    <th style={{ width: "12%" }}>
                      Qty Accepted <span className="grn-field-required">*</span>
                    </th>
                    <th style={{ width: "10%" }}>Qty Rejected</th>
                    <th style={{ width: "17%" }}>Item Note / Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {lines && lines.length > 0 ? (
                    lines.map((l, idx) => {
                      const id = l.Id || l.poLineId || `line_${idx}`;
                      const remaining = Number(
                        l.RemainingQuantity ?? l.Quantity ?? l.quantity ?? 0,
                      );
                      const poQty = Number(l.Quantity ?? l.OrderedQuantity ?? remaining);
                      const itemName =
                        l.MaterialName || l.ItemDescription || l.ItemName || `Item #${idx + 1}`;
                      const uom = l.PackagingName || l.UnitOfMeasure || l.UOM || "Units";

                      return (
                        <tr key={id}>
                          <td className="grn-item-name-cell">{itemName}</td>
                          <td>
                            <span className="grn-uom-badge">{uom}</span>
                          </td>
                          <td>
                            <span className="grn-qty-badge">{poQty}</span>
                          </td>
                          <td>
                            <span className="grn-qty-badge" style={{ color: "#0f766e" }}>
                              {remaining}
                            </span>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="grn-table-input"
                              min="0"
                              max={remaining}
                              value={qtyReceivedMap[id] ?? ""}
                              onChange={(e) => handleQtyReceivedChange(id, e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="grn-table-input"
                              min="0"
                              max={qtyReceivedMap[id] || remaining}
                              value={qtyAcceptedMap[id] ?? ""}
                              onChange={(e) => handleQtyAcceptedChange(id, e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className={`grn-table-input ${
                                Number(qtyRejectedMap[id]) > 0 ? "rejected-input" : ""
                              }`}
                              min="0"
                              value={qtyRejectedMap[id] ?? 0}
                              onChange={(e) => handleQtyRejectedChange(id, e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="grn-table-remarks-input"
                              placeholder="e.g. OK / remarks"
                              value={remarksMap[id] || ""}
                              onChange={(e) => handleRemarksChange(id, e.target.value)}
                            />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: "center", padding: "28px" }}>
                        <span style={{ color: "#64748b", fontStyle: "italic" }}>
                          {selectedPoId
                            ? "No item lines found in this Purchase Order."
                            : "Please select a Purchase Order above to view and receive item lines."}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* SECTION 3: Evidence & General Remarks Card */}
            <div className="grn-evidence-card">
              <div className="grn-evidence-header">
                <h4 className="grn-evidence-header-title">
                  <FiPaperclip size={18} color="#0d9488" />
                  <span>Evidence & General Remarks</span>
                </h4>
                <span className="grn-evidence-header-badge">Delivery Verification</span>
              </div>

              {/* Main Row: Upload Card (Left) & Description (Right) */}
              <div className="grn-evidence-main-row">
                <div className="grn-field-group">
                  <label className="grn-field-label">
                    Approver Users <span className="grn-field-required">*</span>
                  </label>
                  <div className="grn-approvers-container" ref={approverDropdownRef}>
                    <div
                      className="grn-approvers-trigger-box"
                      onClick={() => setShowApproverDropdown((prev) => !prev)}
                    >
                      {approverUserIds.length === 0 ? (
                        <span style={{ color: "#94a3b8", fontSize: "13px" }}>
                          Select approver users...
                        </span>
                      ) : (
                        approverUserIds.map((userId) => {
                          const user = [
                            ...populateUsers,
                            {
                              Id: session?.LoginId,
                              FullName: session?.UserName,
                              Email: session?.Email,
                            },
                          ]?.find((u) => u?.Id === userId);

                          return (
                            <span key={userId} className="grn-approver-pill">
                              <span>{user?.FullName || userId}</span>
                              <span
                                className="grn-approver-remove-x"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeApprover(userId);
                                }}
                              >
                                &times;
                              </span>
                            </span>
                          );
                        })
                      )}
                    </div>

                    {showApproverDropdown && (
                      <div className="grn-approver-dropdown-menu">
                        <input
                          type="text"
                          className="grn-approver-search-input"
                          placeholder="Search users by name or email..."
                          value={approverSearchTerm}
                          onChange={(e) => setApproverSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div>
                          {filteredUsers?.map((user) => (
                            <div
                              key={user.Id}
                              className={`grn-approver-option-item ${
                                approverUserIds.includes(user.Id) ? "selected" : ""
                              }`}
                              onClick={() => toggleApprover(user.Id)}
                            >
                              <input
                                type="checkbox"
                                checked={approverUserIds.includes(user.Id)}
                                readOnly
                                style={{ margin: 0, cursor: "pointer" }}
                              />
                              <div>
                                <div style={{ fontWeight: 600, fontSize: "12.5px" }}>
                                  {user.FullName}
                                </div>
                                <div style={{ fontSize: "11.5px", color: "#64748b" }}>
                                  {user.Email}
                                </div>
                              </div>
                            </div>
                          ))}
                          {filteredUsers.length === 0 && (
                            <div style={{ padding: "10px", textAlign: "center", color: "#94a3b8" }}>
                              No users found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grn-upload-card-container">
                  <label className="grn-field-label mb-2">Upload Delivery Evidence</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        processSelectedFile(file);
                      }
                    }}
                  />
                  {!attachedFile ? (
                    <div
                      className={`document-upload-box grn-upload-dropzone ${isDragging ? "drag-active" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          processSelectedFile(file);
                        }
                      }}
                    >
                      <div className="grn-upload-icon-circle document-upload-icon">
                        <FiUploadCloud size={24} />
                      </div>
                      <div className="grn-upload-primary-text document-upload-copy">
                        Click to browse or drag &amp; drop photo
                      </div>
                      <div className="grn-upload-subtext">
                        Attach delivery challan, bilty, or weighbridge slip (JPG, PNG, PDF up to
                        10MB)
                      </div>
                      <span className="grn-upload-btn-pill document-upload-file">
                        <FiUploadCloud size={14} />
                        <span>Upload / Snap Photo</span>
                      </span>
                    </div>
                  ) : (
                    <div className="grn-file-attached-card document-upload-box">
                      <div className="grn-file-preview-thumb-box">
                        {filePreview ? (
                          <img
                            src={filePreview}
                            alt="Receipt preview"
                            className="grn-file-preview-thumb-img"
                          />
                        ) : (
                          <FiFileText size={38} color="#0d9488" />
                        )}
                      </div>
                      <div className="grn-file-info-col">
                        <div
                          className="grn-file-name-title document-upload-copy"
                          title={attachedFile.name}
                        >
                          {attachedFile.name}
                        </div>
                        <div className="grn-file-meta-row document-upload-file">
                          <span>{formatFileSize(attachedFile.size)}</span>
                          <span>•</span>
                          <span className="grn-file-status-badge">
                            <FiCheck size={12} />
                            <span>Ready</span>
                          </span>
                        </div>
                        <button
                          type="button"
                          className="grn-file-remove-btn document-upload-remove"
                          onClick={handleRemoveFile}
                        >
                          <FiTrash2 size={13} />
                          <span>Remove</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grn-description-card-container">
                  <TextAreaField
                    label="General Remarks:"
                    name="generalRemarks"
                    value={generalRemarks}
                    onChange={(e) => setGeneralRemarks(e.target.value)}
                    placeholder="e.g. Delivered via 10-wheeler Bedford truck at Gate 2. Materials inspected by storekeeper and found intact."
                    required
                    rows={4}
                    stacked
                    wrapperCol="col-12"
                  />
                </div>
              </div>
            </div>

            {/* Footer Action Buttons */}
            <div className="grn-actions-footer">
              <div className="float-right  mr-md-3">
                <ActionButton text="Cancel" variant="cancel" onClick={goBackToGrn} />
                <ActionButton
                  text="Save"
                  icon={FiUploadCloud}
                  onClick={handleSubmit}
                  type="submit"
                  isLoading={submitting}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
